import React, { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClockRotateLeft,
  faMicrophone,
  faPaperPlane,
  faCircleNotch,
  faXmark,
  faPlus,
  faArrowRight,
  faKeyboard,
  faHandPointer,
  faCheck,
  faWandMagicSparkles,
  faArrowsUpDown,
  faLock,
  faMoneyBillTransfer,
  faPlaneDeparture,
  faScaleBalanced,
  faClipboardList,
  faCircleQuestion,
  faEnvelopeOpenText,
  faPenNib,
  faCompass,
  faFileLines,
  faPaperclip,
} from "@fortawesome/free-solid-svg-icons";
import {
  streamCopilotChat,
  listCopilotSessions,
  getCopilotSession,
  copilotReply,
  copilotRequestRates,
  copilotNudges,
  copilotBulkChase,
  copilotBulkStage,
  fetchCopilotAttachment,
  analyzeCopilotUpload,
} from "../../Api/copilotApi";
import MicButton from "../voice/MicButton";
import AnimatedLoader from "../../motionKit/AnimatedLoader";
import { spotlightRef, setReactValue } from "../voice/spotlight";
import {
  scanActionable,
  clickControl,
  setSelect,
  setValue as setControlValue,
  goToPage,
  detailPathForRef,
  forceClick,
  forceSet,
} from "../voice/copilotControls";
import { reveal, staggerContainer, staggerItem, popIn } from "../motion/presets";
import { upsertStickyNote } from "../../Api";

// Human-readable labels for the raw tool names the model emits (chips otherwise
// read like API traffic: "navigateTo", "goToPage").
const TOOL_LABELS = {
  navigateTo: "Navigating",
  goToPage: "Turning page",
  openDetail: "Opening",
  highlightElement: "Pointing",
  clickControl: "Clicking",
  setSelect: "Choosing",
  setValue: "Typing",
  findQuote: "Searching",
  convertCurrency: "Checking rate",
  lookupAirline: "Looking up airline",
  lookupShipper: "Looking up shipper",
  quoteAirlineStatus: "Checking rates",
  summarizeQuote: "Summarizing",
  listMissingFields: "Reviewing quote",
  calcChargeableWeight: "Computing weight",
  resolveIataCode: "Resolving airport",
  proposeAddNote: "Drafting note",
  scroll: "Scrolling",
  goBack: "Going back",
  refreshPage: "Refreshing",
  closePanel: "Closing",
  clearField: "Clearing",
};
const humanTool = (n) =>
  TOOL_LABELS[n] ||
  String(n || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/^./, (c) => c.toUpperCase());

// One FontAwesome family for action chips (was serif Unicode dingbats ☞⌨✦).
const ACTION_ICONS = {
  navigate: faArrowRight,
  fill: faKeyboard,
  click: faHandPointer,
  done: faCheck,
  error: faXmark,
  highlight: faWandMagicSparkles,
  scroll: faArrowsUpDown,
};

/* Client-action execution: the copilot's navigateTo/highlightElement tools
 * arrive as SSE {type:"client_action"} events; the app performs them here
 * through react-router + the spotlight util — same session, same permissions
 * as any click. Read/point only; there are no mutating actions. */
const NAV_CLIENT_PATHS = {
  dashboard: (r) => `/${r}/dashboard`,
  allQuotes: (r) => `/${r}/all-quotes`,
  manualQueries: (r) => `/${r}/manual-queries`,
  emailQueries: (r) => `/${r}/incomplete-queries`,
  quoteDetail: (r, id) => `/${r}/newQuotePage/${id}`,
  manualQueryDetail: (r, id) => `/${r}/manual-query/${id}`,
  generateDocuments: (r, id) => `/${r}/${id}/generate-documents`,
  airlines: (r) => `/${r}/airlines`,
  agents: (r) => `/${r}/agents`,
  operators: (r) => `/${r}/operators`,
  templates: (r) => `/${r}/templates`,
  // admins (admin-only), transactions (admin/user), awbStock (operator-only) —
  // segments verified against AuthRoot.jsx (/admin/admins, /admin/transactions,
  // /operator/awb-stock). `r` is the apiRole (admin | operator).
  admins: (r) => `/${r}/admins`,
  transactions: (r) => `/${r}/transactions`,
  awbStock: (r) => `/${r}/awb-stock`,
};

// Compact snapshot of what's currently rendered in the main content area — the
// copilot's "eyes". Read-only, already-authenticated content the operator is
// looking at (no new data exposure). Capped so it never bloats the prompt.
//
// Key detail: when a results/subjects list is on screen, we emit its rows as an
// explicit NUMBERED list in DOM (visual) order. Without this the model got a
// raw text blob and hallucinated answers like "the 5th entry is …". The numbers
// let it answer "the Nth entry" accurately.
function capturePageSnapshot() {
  try {
    const main =
      document.querySelector("main, [data-scroll-root]") || document.body;

    let structured = "";
    const listHost = document.querySelector(
      '[data-tour="eq-subject-list"], [data-tour="mq-query-list"]'
    );
    if (listHost) {
      // Outermost row buttons only (skip nested action buttons), visible.
      let rows = Array.from(
        listHost.querySelectorAll('[role="button"]')
      ).filter(
        (el) =>
          el.offsetParent !== null &&
          !el.parentElement?.closest('[role="button"]')
      );
      // Fallback for card lists without role=button rows.
      if (!rows.length) {
        rows = Array.from(listHost.children).filter(
          (el) => el.offsetParent !== null && (el.innerText || "").trim()
        );
      }
      if (rows.length) {
        const items = rows.slice(0, 25).map((r, i) => {
          const t = (r.innerText || "").replace(/\s+/g, " ").trim().slice(0, 130);
          return `${i + 1}. ${t}`;
        });
        structured =
          "VISIBLE LIST — the rows currently shown, in order top-to-bottom. Use these EXACT rows to answer questions like \"the Nth entry\"; do not invent rows:\n" +
          items.join("\n") +
          "\n\n";
      }
    }

    // The list rows are inside <main>, so without excluding the list host the
    // same row text would ride along TWICE (VISIBLE LIST + this blob) — ~500
    // tokens of pure duplication per message on list pages. Clone-and-strip
    // rather than mutating the live DOM.
    let textSource = main;
    if (structured && listHost && main.contains(listHost)) {
      const clone = main.cloneNode(true);
      const cloneList = clone.querySelector(
        '[data-tour="eq-subject-list"], [data-tour="mq-query-list"]'
      );
      if (cloneList) cloneList.remove();
      textSource = clone;
    }
    let text = (textSource.innerText || textSource.textContent || "")
      .replace(/[ \t]+/g, " ")
      .replace(/\n[ \t]*\n[ \t]*(\n)+/g, "\n\n")
      .trim();
    const CAP = structured ? 800 : 2200;
    if (text.length > CAP) text = text.slice(0, CAP) + "…";
    // 2500 matches the backend's hard slice — anything past it is upload waste.
    return (structured + text).slice(0, 2500);
  } catch {
    return "";
  }
}

// Find the element that actually scrolls. FK's lists scroll inside their own
// nested overflow container (not the window/main), so "scroll the list" must
// target THAT element or nothing moves.
function isScrollable(el) {
  if (!el || el.nodeType !== 1) return false;
  const s = window.getComputedStyle(el);
  return (
    /(auto|scroll)/.test(s.overflowY) && el.scrollHeight - el.clientHeight > 20
  );
}

function scrollApp(direction) {
  let scroller = null;
  // Prefer the on-screen results list's own scroll container.
  const listHost = document.querySelector(
    '[data-tour="eq-subject-list"], [data-tour="mq-query-list"]'
  );
  if (listHost) {
    // The scroll area is usually a descendant of the list host; fall back to an
    // ancestor, then to the host itself.
    scroller =
      Array.from(listHost.querySelectorAll("*")).find(isScrollable) ||
      (() => {
        let el = listHost;
        while (el && el !== document.body) {
          if (isScrollable(el)) return el;
          el = el.parentElement;
        }
        return null;
      })();
  }
  // Fallback: the largest scrollable main region, then the window.
  if (!scroller) {
    scroller =
      Array.from(document.querySelectorAll("main, [data-scroll-root]")).find(
        isScrollable
      ) ||
      document.scrollingElement ||
      document.documentElement;
  }
  const chunk = Math.round((scroller.clientHeight || window.innerHeight) * 0.7);
  if (direction === "top") scroller.scrollTo({ top: 0, behavior: "smooth" });
  else if (direction === "bottom")
    scroller.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
  else
    scroller.scrollBy({
      top: direction === "up" ? -chunk : chunk,
      behavior: "smooth",
    });
}

const NAV_PAGE_LABELS = {
  dashboard: "Dashboard",
  allQuotes: "All Quotes",
  manualQueries: "Manual Queries",
  emailQueries: "Email Queries",
  quoteDetail: "the quote workspace",
  manualQueryDetail: "the query detail",
  generateDocuments: "Document Studio",
  airlines: "Airlines",
  agents: "Agents",
  operators: "Operators",
  templates: "Templates",
  admins: "Admins",
  transactions: "Transactions",
  awbStock: "AWB Stock",
};

const SESSION_KEY = "fk_copilot_session_id";

/* Tappable attachment card: rendered when the bot lists a thread's files.
 * Tap a row to select it → Analyze / Preview actions appear for that file.
 * Selection is per-card local state; the actions call back into the chat
 * (Analyze = a normal chat turn, Preview = authenticated blob → new tab). */
function AttachmentCard({ card, disabled, onAnalyze, onPreview }) {
  const [selected, setSelected] = useState(null);
  const [busyIndex, setBusyIndex] = useState(null);
  const files = card?.files || [];
  if (!files.length) return null;
  return (
    <div className="mt-1.5 overflow-hidden rounded-xl ring-1 ring-violet-200/70 dark:ring-violet-500/25">
      <p className="bg-violet-50/70 px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-violet-700 dark:bg-violet-900/20 dark:text-violet-300">
        {files.length} attachment{files.length === 1 ? "" : "s"} — tap one
      </p>
      <ul className="max-h-52 overflow-y-auto bg-white dark:bg-[oklch(0.225_0.026_264)]">
        {files.map((f) => {
          const isSel = selected === f.index;
          return (
            <li key={f.index} className="border-t border-slate-100 first:border-t-0 dark:border-[oklch(0.3_0.02_264)]">
              <button
                type="button"
                onClick={() => setSelected(isSel ? null : f.index)}
                className={`flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs transition ${
                  isSel
                    ? "bg-violet-50 dark:bg-violet-900/25"
                    : "hover:bg-slate-50 dark:hover:bg-[oklch(0.26_0.028_264/0.6)]"
                }`}
              >
                <FontAwesomeIcon icon={faFileLines} className="shrink-0 text-[11px] text-rose-500" />
                <span className="min-w-0 flex-1 truncate text-slate-700 dark:text-slate-200">
                  {f.filename}
                </span>
                <span className="shrink-0 rounded-full bg-slate-100 px-1.5 py-0.5 text-[9px] font-medium text-slate-500 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-400">
                  {f.kind} · {f.sizeKB} KB
                </span>
              </button>
              {isSel && (
                <div className="flex items-center gap-1.5 bg-violet-50/60 px-2.5 pb-2 pt-0.5 dark:bg-violet-900/15">
                  <button
                    type="button"
                    disabled={disabled}
                    onClick={() => {
                      setSelected(null);
                      onAnalyze(f);
                    }}
                    className="inline-flex items-center gap-1 rounded-lg bg-violet-600 px-2.5 py-1 text-[10px] font-semibold text-white transition hover:bg-violet-500 disabled:opacity-60"
                  >
                    <FontAwesomeIcon icon={faWandMagicSparkles} className="text-[9px]" />
                    Analyze this
                  </button>
                  <button
                    type="button"
                    disabled={busyIndex === f.index}
                    onClick={async () => {
                      setBusyIndex(f.index);
                      try {
                        await onPreview(f);
                      } finally {
                        setBusyIndex(null);
                      }
                    }}
                    className="inline-flex items-center gap-1 rounded-lg border border-violet-300 px-2.5 py-1 text-[10px] font-semibold text-violet-700 transition hover:bg-violet-100 disabled:opacity-60 dark:border-violet-500/40 dark:text-violet-300 dark:hover:bg-violet-900/30"
                  >
                    {busyIndex === f.index ? (
                      <FontAwesomeIcon icon={faCircleNotch} spin className="text-[9px]" />
                    ) : (
                      <FontAwesomeIcon icon={faFileLines} className="text-[9px]" />
                    )}
                    Preview
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* Quote id from the URL itself (not useParams): this component mounts at the
 * AuthRoot layout level, where child-route params aren't visible. Covers the
 * quote workspace, manual-query detail, and Document Studio routes. */
function useRouteQuoteId() {
  const { pathname } = useLocation();
  return useMemo(() => {
    const m =
      pathname.match(
        /\/(?:newQuotePage|quotePage|manual-query)\/([a-f0-9]{24})/i
      ) || pathname.match(/\/([a-f0-9]{24})\/generate-documents/i);
    return m ? m[1] : undefined;
  }, [pathname]);
}

function useCopilotGreeting() {
  const { pathname } = useLocation();
  const id = useRouteQuoteId();
  return useMemo(() => {
    if (
      (pathname.includes("/newQuotePage/") || pathname.includes("/quotePage/")) &&
      id
    ) {
      const shortId = String(id).slice(-6);
      return `I can work this quote …${shortId} with you — summarize it, list what's missing, show who replied with rates, or draft and send a reply. Say it or type it.`;
    }
    return "I can drive FreightKit for you — open pages, search, fill fields, click buttons — and answer freight questions. Say it or type it.";
  }, [pathname, id]);
}

function useQuickActions() {
  const { pathname } = useLocation();
  const id = useRouteQuoteId();
  return useMemo(() => {
    const onQuote =
      (pathname.includes("/newQuotePage/") || pathname.includes("/quotePage/")) && id;
    if (onQuote) {
      return [
        { icon: faClipboardList, label: "Summarize this quote", text: "Summarize this quote." },
        { icon: faCircleQuestion, label: "What's missing?", text: "List the fields still missing on this quote." },
        {
          icon: faEnvelopeOpenText,
          label: "Who has replied?",
          text: "Which airlines and agents have responded with rates on this quote?",
        },
        {
          icon: faPenNib,
          label: "Draft a reply",
          text: "Draft a short acknowledgement reply to the customer for this quote.",
        },
      ];
    }
    if (pathname.includes("/incomplete-queries") || pathname.includes("/all-quotes")) {
      return [
        { icon: faCompass, label: "Go to Email Queries", text: "Take me to the email queries page." },
        { icon: faMoneyBillTransfer, label: "Convert 5000 USD → AED", text: "Convert 5000 USD to AED at today's rate." },
        { icon: faPlaneDeparture, label: "Look up an airline", text: "Look up airline Emirates." },
        { icon: faScaleBalanced, label: "Chargeable weight", text: "Help me compute chargeable weight — I'll give you dimensions." },
      ];
    }
    return [
      { icon: faCompass, label: "Take me to All Quotes", text: "Take me to the all quotes page." },
      { icon: faFileLines, label: "Open Document Studio", text: "Open document studio for this quote." },
      { icon: faMoneyBillTransfer, label: "Convert currency", text: "Convert 5000 USD to AED at today's rate." },
      { icon: faScaleBalanced, label: "Chargeable weight", text: "Help me compute chargeable weight — I'll give you dimensions." },
    ];
  }, [pathname, id]);
}

function sessionIdForTab() {
  if (typeof window === "undefined" || !window.sessionStorage) {
    return crypto.randomUUID();
  }
  let sid = window.sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = crypto.randomUUID();
    window.sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
}

export default function AIChatBot() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUsage, setLastUsage] = useState(null);
  // Voice: mic state drives the placeholder; transcripts auto-send.
  const [voiceStatus, setVoiceStatus] = useState("idle");
  const [voiceError, setVoiceError] = useState("");
  // A write the bot proposed, awaiting the operator's confirm. { intent, params, summary, confirmLabel }
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  // Editable draft for a sendReply/bulkChase confirm — the operator can tweak
  // the drafted message before sending. Seeded from the proposed body when the
  // card opens.
  const [replyDraft, setReplyDraft] = useState("");
  useEffect(() => {
    if (
      pendingConfirm?.intent === "sendReply" ||
      pendingConfirm?.intent === "bulkChase"
    ) {
      setReplyDraft(String(pendingConfirm.params?.body || ""));
    }
  }, [pendingConfirm]);
  // Bumped whenever a client action executes → one-shot success flourish.
  const [flourish, setFlourish] = useState(0);
  // Confirmed WRITES (reply sent, bulk done, note saved) get the full success
  // burst overlay for ~2s — a bigger moment than the subtle flourish glow.
  const [burstKey, setBurstKey] = useState(0);
  useEffect(() => {
    if (!burstKey) return undefined;
    const t = setTimeout(() => setBurstKey(0), 2100);
    return () => clearTimeout(t);
  }, [burstKey]);
  const [flourishOn, setFlourishOn] = useState(false);
  useEffect(() => {
    if (flourish === 0) return undefined;
    setFlourishOn(true);
    const t = setTimeout(() => setFlourishOn(false), 550);
    return () => clearTimeout(t);
  }, [flourish]);
  // A message composed while a reply is still streaming — queued and flushed
  // when the stream ends, shown to the operator so it never feels dropped.
  const [queuedPreview, setQueuedPreview] = useState("");
  const [showCoach, setShowCoach] = useState(false);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const messagesEndRef = useRef(null);
  const listRef = useRef(null);
  const stickyRef = useRef(true); // is the operator pinned to the latest message?
  const abortRef = useRef(null);
  const sessionIdRef = useRef(
    typeof crypto !== "undefined" && crypto.randomUUID
      ? sessionIdForTab()
      : String(Date.now())
  );
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  // Proactive nudges (MOVE 3): a stashed { text, suggestedPrompt } fetched while
  // the panel is CLOSED. Surfaced as ONE subtle chip when the operator opens the
  // panel. `nudgeSeenRef` tracks quoteIds already nudged this session so we never
  // nag the same quote twice; keyed by quoteId so re-navigating doesn't re-fire.
  const [stashedNudge, setStashedNudge] = useState(null);
  const nudgeSeenRef = useRef(new Set());
  const greeting = useCopilotGreeting();
  const quickActions = useQuickActions();
  const location = useLocation();
  const routeQuoteId = useRouteQuoteId();
  const userLoggedIn = useSelector((s) => s.reducer.userLoggedIn);
  const loginToken = useSelector((s) => s.reducer.userToken);
  const role = userLoggedIn?.role;
  // Voice FAB / Alt+M: increments arm the input MicButton on panel open.
  // Reset on close so re-opening never surprise-records.
  // (Declared after loginToken/role — the dep arrays read them at render time.)
  const [voiceArm, setVoiceArm] = useState(0);
  // Mirror of messages for callbacks that must read it without re-subscribing.
  const messagesRef = useRef([]);
  messagesRef.current = messages;
  // Was the current turn started by voice? Drives auto-arm on the confirm card
  // so "yes"/"cancel" works hands-free only when the operator was talking.
  const voiceTurnRef = useRef(false);
  // One bounded auto-retry per user turn: if a *driving* action (setValue /
  // clickControl / setSelect / goToPage / navigate / openDetail) fails, we fire
  // exactly one silent follow-up turn asking the model to re-check and retry.
  // Reset to false at the start of every user-initiated send so it can NEVER
  // loop (an auto-retry turn leaves it true → no second retry).
  const autoRetryUsedRef = useRef(false);
  // Message queued by a settled turn that hit a driving failure; flushed as a
  // hidden auto-retry turn once `loading` clears (mirrors pendingSendRef).
  const autoRetryMsgRef = useRef("");
  const openWithVoice = useCallback(
    (source = "fab") => {
      setOpen(true);
      const allowed =
        loginToken && ["admin", "superAdmin", "operator"].includes(role || "");
      if (!allowed) {
        // No mic will mount — surface why instead of a dead pre-armed panel.
        setVoiceError("Sign in as an operator or admin to use voice.");
        setTimeout(() => setVoiceError(""), 5000);
        return;
      }
      // Alt+M is explicit voice intent → arm the mic. The FAB just OPENS the
      // panel: auto-recording on open surprised operators who only wanted to
      // read the chat — recording starts only from the mic button (or hotkey).
      if (source === "hotkey") setVoiceArm((v) => v + 1);
    },
    [loginToken, role]
  );
  const closePanel = useCallback(() => {
    try {
      abortRef.current?.abort();
    } catch {
      /* */
    }
    setOpen(false);
    requestAnimationFrame(() => fabRef.current?.focus());
  }, []);
  useEffect(() => {
    if (!open) setVoiceArm(0);
  }, [open]);
  useEffect(() => {
    const onKey = (e) => {
      if (e.repeat) return; // ignore key-repeat (held chord)
      if (!(e.altKey && e.code === "KeyM") || e.metaKey || e.ctrlKey) return;
      // Alt+M types a character (µ) on some layouts — don't hijack it while
      // the user is typing in a field or the editor.
      const t = e.target;
      if (
        t &&
        (t.isContentEditable ||
          /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName || ""))
      ) {
        return;
      }
      e.preventDefault();
      openWithVoice("hotkey");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openWithVoice]);
  // Escape: cancel a pending confirm first, else close the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (pendingConfirm) {
        if (!confirmBusy) setPendingConfirm(null);
        return;
      }
      closePanel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, pendingConfirm, confirmBusy, closePanel]);
  // First-run coach mark (once ever): explains the hot-mic contract.
  useEffect(() => {
    if (!open) return;
    try {
      if (!window.localStorage?.getItem("fk_copilot_seen")) setShowCoach(true);
    } catch {
      /* */
    }
  }, [open]);
  const dismissCoach = useCallback(() => {
    setShowCoach(false);
    try {
      window.localStorage?.setItem("fk_copilot_seen", "1");
    } catch {
      /* */
    }
  }, []);
  // Selected conversation on a split-pane list page (Email Queries / All
  // Quotes): those URLs carry no quote id, so the list broadcasts its
  // selection and it becomes the quote context — replies/rate-requests work
  // from there without opening the detail page.
  const [selectedListQuoteId, setSelectedListQuoteId] = useState(null);
  useEffect(() => {
    const onSel = (e) => setSelectedListQuoteId(e?.detail?.quoteId || null);
    window.addEventListener("fk:copilot-selected-quote", onSel);
    return () => window.removeEventListener("fk:copilot-selected-quote", onSel);
  }, []);

  const pageContext = useMemo(() => {
    return {
      route: location.pathname,
      // Quote context: a detail-page URL wins; otherwise the conversation
      // selected in a list's split pane.
      quoteId: routeQuoteId || selectedListQuoteId || undefined,
      threadId: undefined,
    };
  }, [location.pathname, routeQuoteId, selectedListQuoteId]);

  useEffect(() => {
    if (open) {
      setMessages((m) => {
        if (m.length > 0) return m;
        return [{ from: "assistant", text: greeting, tools: [] }];
      });
    }
  }, [open, greeting]);

  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // Scroll-follow: while the operator is pinned near the bottom, keep the newest
  // content in view WITHOUT a smooth animation on every streamed token (that
  // rubber-bands and hijacks a reader who scrolled up). Instant scrollTop while
  // pinned; leave them alone once they scroll away.
  const onListScroll = useCallback((e) => {
    const el = e.currentTarget;
    stickyRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }, []);
  useEffect(() => {
    if (!open) return;
    if (!stickyRef.current) return;
    const el = listRef.current;
    if (el) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }, [messages, open]);

  const showPrivacy =
    pageContext.quoteId && String(pageContext.quoteId).length > 0;
  const shortId = showPrivacy ? String(pageContext.quoteId).slice(-6) : "";

  const canUse =
    loginToken && ["admin", "superAdmin", "operator"].includes(role || "");

  // A document "pinned" from the conversation view (eye button on an email
  // attachment). While set, every question this panel sends is routed as a
  // question about THAT file (the model calls analyzeAttachment on it).
  const [askDoc, setAskDoc] = useState(null);
  useEffect(() => {
    const onAskDoc = (e) => {
      const d = e?.detail || {};
      if (!d.filename) return;
      setAskDoc({
        quoteId: d.quoteId || routeQuoteId || null,
        filename: String(d.filename),
        threadId: d.threadId || null,
      });
      setOpen(true);
    };
    window.addEventListener("fk:copilot-ask-doc", onAskDoc);
    return () => window.removeEventListener("fk:copilot-ask-doc", onAskDoc);
  }, [routeQuoteId]);

  const sendMessage = useCallback(
    async (override, opts) => {
      const isAutoRetry = !!opts?.isAutoRetry;
      const t = (typeof override === "string" ? override : input).trim();
      if (!t) return;
      if (loading) return;
      if (!loginToken || !["admin", "superAdmin", "operator"].includes(role || "")) {
        setMessages((m) => [
          ...m,
          {
            from: "assistant",
            text: "Sign in as an operator or admin to use Copilot.",
            tools: [],
          },
        ]);
        return;
      }
      // A genuine user turn re-arms the single auto-retry budget. An auto-retry
      // turn deliberately does NOT reset it — that's what caps retries at one.
      if (!isAutoRetry) autoRetryUsedRef.current = false;
      if (typeof override !== "string") setInput("");
      setLoading(true);
      if (abortRef.current) {
        try {
          abortRef.current.abort();
        } catch {
          /* */
        }
      }
      const ac = new AbortController();
      abortRef.current = ac;
      setMessages((m) => [
        ...m,
        // An auto-retry isn't the operator talking — render it as a subtle
        // "system" status line ("Retrying a different way…"), never a user
        // bubble, so the panel doesn't pretend the operator asked again.
        isAutoRetry
          ? { from: "system", text: "Retrying a different way…", tools: [] }
          : { from: "user", text: t, tools: [] },
        { from: "assistant", text: "", tools: [], streaming: true },
      ]);
      let assistant = "";
      let usage = 0;
      const apiRole = role === "superAdmin" ? "admin" : role;
      // Pinned document (eye button): the bubble shows the operator's words
      // (t); the model additionally learns which file is open so it can read
      // it. Phrased so the model uses JUDGEMENT — analyze only when the message
      // is about the document, otherwise answer normally — so a pinned doc
      // doesn't force analysis onto unrelated follow-ups. The quote is also
      // forced into pageContext.quoteId, so analyzeAttachment resolves it via
      // ctx even without the model threading it.
      const modelText = askDoc?.filename
        ? `[The operator has this attachment open: "${askDoc.filename}"${
            askDoc.quoteId ? ` on quote ${askDoc.quoteId}` : ""
          }. If their message is about this document, answer it by calling analyzeAttachment with filename "${
            askDoc.filename
          }". Otherwise answer normally.]\n${t}`
        : t;
      const pendingActions = [];
      // Navigation/sequence actions dispatched mid-stream (overlap with the model
      // streaming). We keep their promises so the tail waits for them to settle
      // before running the buffered follow-ups.
      const earlyNav = [];

      // Collect driving-action failures this turn so we can fire ONE bounded
      // auto-retry (MOVE 2). A "driving" action is one that manipulates the app
      // (navigate/openDetail/setValue/clickControl/setSelect/goToPage/etc.) — as
      // opposed to a confirm card or a read-only chip. `emitChip` records the
      // failed chip's label while `drivingActive` is set by runSafe.
      const drivingFailures = [];
      let drivingActive = false;

      // Show a result chip + flourish (flourish only on success).
      const emitChip = (chip) => {
        if (!chip) return;
        if (chip.kind !== "error") setFlourish((f) => f + 1);
        else if (drivingActive) drivingFailures.push(chip.label);
        appendActionChip(chip);
      };
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

      // Settle after a route change: resolve the INSTANT the new view's target is
      // present (search input / pagination bar / spinner gone) via a
      // MutationObserver on <main>, hard-capped so a batch never stalls.
      const settleAfterNav = (capMs = 2000) =>
        new Promise((resolve) => {
          const targetReady = () => {
            const main = document.querySelector("main, [data-scroll-root]");
            if (!main) return false;
            const hasSearch = !!main.querySelector(
              'input[type="search"], input[placeholder*="earch" i], [data-tour$="search-bar"]'
            );
            const hasPager = !!document.querySelector(
              '[data-copilot="pagination-bar"]'
            );
            const spinnerGone = !main.querySelector(
              '.animate-spin, [role="progressbar"], [aria-busy="true"]'
            );
            return (hasSearch || hasPager) && spinnerGone;
          };
          if (targetReady()) {
            resolve();
            return;
          }
          let done = false;
          const finish = () => {
            if (done) return;
            done = true;
            try {
              obs.disconnect();
            } catch {
              /* */
            }
            clearTimeout(timer);
            resolve();
          };
          const root =
            document.querySelector("main, [data-scroll-root]") || document.body;
          const obs = new MutationObserver(() => {
            if (targetReady()) finish();
          });
          try {
            obs.observe(root, { childList: true, subtree: true, attributes: true });
          } catch {
            /* */
          }
          const timer = setTimeout(finish, capMs);
        });

      // Navigate to a page ref; defer the ✓ chip until AFTER settle and gate it
      // on the URL actually matching the built path (else ✗) — honest nav.
      const doNavigate = async (a) => {
        const build = NAV_CLIENT_PATHS[a.page];
        const path = build ? build(apiRole, a.quoteId) : null;
        if (!path) return { ok: false, label: `don't know how to open ${a.page}` };
        navigate(path);
        await settleAfterNav();
        const here = window.location.pathname;
        const ok = here === path || here.startsWith(path);
        return ok
          ? { ok: true, label: `Opened ${NAV_PAGE_LABELS[a.page] || a.page}` }
          : { ok: false, label: `couldn't open ${NAV_PAGE_LABELS[a.page] || a.page}` };
      };
      const doOpenDetail = async (a) => {
        const path = detailPathForRef(a.ref);
        if (!path) return { ok: false, label: "couldn't find that row's detail" };
        navigate(path);
        await settleAfterNav();
        const ok = window.location.pathname.startsWith(path.split("?")[0]);
        return ok
          ? { ok: true, label: "Opened full detail" }
          : { ok: false, label: "couldn't open that detail" };
      };

      // Run ONE non-navigation action, verifying its terminal state before the
      // chip. Navigation kinds (navigate/openDetail) are handled by their own
      // async helpers above so their chip can be gated on the settled URL.
      const runAct = async (a) => {
        if (a.type === "setValue" && a.ref) {
          const r = await setControlValue(a.ref, a.value, { isReply: !!a.isReply });
          return { ...r, kind: r.ok ? "fill" : "error" };
        }
        if (a.type === "clearField" && a.ref) {
          const r = await setControlValue(a.ref, "");
          return {
            ...r,
            kind: r.ok ? "fill" : "error",
            label: r.ok ? "Cleared" : r.label,
          };
        }
        if (a.type === "setSelect" && a.ref) {
          const r = await setSelect(a.ref, a.value);
          if (r.needsConfirm) {
            setPendingConfirm({ intent: "replayControl", ...r.confirm });
            return null; // confirm-gated: no chip yet
          }
          return { ...r, kind: r.ok ? "click" : "error" };
        }
        if (a.type === "clickControl" && a.ref) {
          const r = await clickControl(a.ref);
          if (r.needsConfirm) {
            setPendingConfirm({ intent: "replayControl", ...r.confirm });
            return null;
          }
          return { ...r, kind: r.ok ? "click" : "error" };
        }
        if (a.type === "goToPage") {
          const r = await goToPage(a.page);
          return { ...r, kind: r.ok ? "click" : "error" };
        }
        if (a.type === "highlight" && a.ref) {
          const r = spotlightRef(a.ref);
          return { ...r, kind: r.ok ? "highlight" : "error" };
        }
        if (a.type === "scroll") {
          scrollApp(a.direction || "down");
          return { ok: true, kind: "scroll", label: `Scrolled ${a.direction || "down"}` };
        }
        if (a.type === "back") {
          navigate(-1);
          return { ok: true, kind: "navigate", label: "Went back" };
        }
        if (a.type === "refresh") {
          setTimeout(() => window.location.reload(), 600);
          return { ok: true, kind: "navigate", label: "Refreshing…" };
        }
        if (a.type === "closePanel") {
          setTimeout(() => setOpen(false), 800);
          return { ok: true, kind: "click", label: "Closed the panel" };
        }
        if (a.type === "confirm" && a.intent) {
          setPendingConfirm(a);
          return null;
        }
        return { ok: false, kind: "error", label: `unsupported action: ${a.type}` };
      };

      // SEMANTIC step: fill the destination page's search box, resolved AFTER
      // navigation (refs from the old page are stale). Waits for the input to
      // mount, then verifies the value PERSISTED — a list page re-inits and can
      // wipe a value typed too early, so we re-apply until it sticks. This is
      // what makes "go to Quotes and search Visual" work in one go.
      const findSearchInput = () =>
        Array.from(document.querySelectorAll('input:not([type="hidden"]), textarea')).find(
          (el) => {
            if (el.closest("[data-copilot-root]")) return false;
            if (el.disabled || el.readOnly || el.offsetParent === null) return false;
            const t = (el.getAttribute("type") || "").toLowerCase();
            const ph = (el.getAttribute("placeholder") || "").toLowerCase();
            const aria = (el.getAttribute("aria-label") || "").toLowerCase();
            return t === "search" || /search|filter|find/.test(ph + " " + aria);
          }
        );
      const doSearch = async (term) => {
        const t = String(term ?? "");
        let input = null;
        for (let i = 0; i < 16 && !input; i += 1) {
          input = findSearchInput();
          if (!input) await sleep(180);
        }
        if (!input) return { ok: false, kind: "error", label: "no search box on this page" };
        // A big list (e.g. All Quotes / 25k rows) clears the search box while its
        // initial data load is in flight — typing too early gets wiped. So wait
        // for the list to SETTLE (spinner gone, rows or an "of N" total present)
        // BEFORE typing; after that the value holds. Cap ~7s so we never stall.
        const listSettled = () => {
          const main = document.querySelector("main, [data-scroll-root]");
          if (!main) return false;
          const busy = main.querySelector(
            '.animate-spin, [role="progressbar"], [aria-busy="true"]'
          );
          const total = /of\s+[\d,]+/i.test(main.innerText || "");
          const rows = document.querySelector(
            '[data-tour="eq-subject-list"] [role="button"], [data-tour="mq-query-list"] [role="button"], [data-copilot-open]'
          );
          return !busy && (total || !!rows);
        };
        for (let i = 0; i < 24 && !listSettled(); i += 1) await sleep(300);
        input = findSearchInput() || input;
        if (!input) return { ok: false, kind: "error", label: "no search box on this page" };
        const has = () =>
          !!input && input.isConnected && String(input.value || "").includes(t);
        setReactValue(input, t);
        // Short stability confirm now that the list is settled.
        let stable = 0;
        for (let i = 0; i < 12 && stable < 2; i += 1) {
          await sleep(300);
          if (!input || !input.isConnected) input = findSearchInput();
          if (!input) break;
          if (has()) stable += 1;
          else {
            stable = 0;
            setReactValue(input, t);
          }
        }
        const ok = has();
        return {
          ok,
          kind: ok ? "fill" : "error",
          label: ok ? `Searched “${t}”` : "couldn't run the search",
        };
      };

      // SEMANTIC step: open the Nth row of the (possibly filtered) list, resolved
      // AFTER the list loads — the only way to "open the 4th result" post-nav,
      // since a pre-nav ref can't point at a row that doesn't exist yet.
      const doOpenRow = async (index) => {
        const n = Math.max(1, Number(index) || 1);
        let path = null;
        for (let i = 0; i < 18 && !path; i += 1) {
          const rows = scanActionable().filter((c) => c.kind === "row-open");
          const target = rows[n - 1]; // scanActionable orders rows top-to-bottom
          if (target) path = detailPathForRef(target.ref);
          if (!path) await sleep(220);
        }
        if (!path) return { ok: false, kind: "error", label: `couldn't find result ${n}` };
        navigate(path);
        await settleAfterNav();
        const ok = window.location.pathname.startsWith(path.split("?")[0]);
        return {
          ok,
          kind: ok ? "navigate" : "error",
          label: ok ? `Opened result ${n}` : `couldn't open result ${n}`,
        };
      };

      // A "sequence" runs its steps STRICTLY in order (navigate/openDetail first,
      // then the acts), settling between the nav and the follow-ups.
      const runSequence = async (a) => {
        const steps = Array.isArray(a.steps) ? a.steps : [];
        for (const step of steps) {
          // Steps carry {action, ...fields}; normalize to the {type,...} shape.
          const s = { ...step, type: step.action || step.type };
          if (s.type === "navigate") {
            emitChip(await doNavigate(s));
          } else if (s.type === "openDetail") {
            emitChip(await doOpenDetail(s));
          } else if (s.type === "search") {
            emitChip(await doSearch(s.value ?? s.term));
          } else if (s.type === "openRow") {
            emitChip(await doOpenRow(s.index ?? s.value));
          } else {
            emitChip(await runAct(s));
          }
        }
      };

      // Public entry for one action. Async — resolves only at terminal state.
      const runOne = async (a) => {
        if (a.type === "navigate") {
          emitChip(await doNavigate(a));
        } else if (a.type === "openDetail" && a.ref) {
          emitChip(await doOpenDetail(a));
        } else if (a.type === "sequence") {
          await runSequence(a);
        } else {
          emitChip(await runAct(a));
        }
      };

      // Driving actions manipulate the app and are safely retriable if they
      // fail (the model can re-scan the controls and try another ref/approach).
      // A `sequence` is driving because its steps are nav/click/type. Confirms
      // (sendReply/requestRates/replayControl) are NOT — those are guarded
      // sends the operator explicitly approved; never silently re-fire them.
      const DRIVING_TYPES = new Set([
        "navigate",
        "openDetail",
        "sequence",
        "setValue",
        "clearField",
        "clickControl",
        "setSelect",
        "goToPage",
      ]);

      // One bad ref must not kill the rest of the batch — chip the failure and
      // keep going. While a driving action runs, mark drivingActive so emitChip
      // records any failure toward the (bounded) auto-retry.
      const runSafe = async (a) => {
        const wasDriving = DRIVING_TYPES.has(a.type);
        drivingActive = wasDriving;
        try {
          await runOne(a);
        } catch (err) {
          emitChip({
            kind: "error",
            label: `${a.type} failed: ${err?.message || "error"}`,
          });
        } finally {
          drivingActive = false;
        }
      };

      // Execute buffered actions in a safe order: page changes first (each
      // settles before the next), then the follow-ups that operate on the new
      // view. sequence carries its own internal ordering.
      const executeActions = async (actions) => {
        const isNav = (a) =>
          a.type === "navigate" || a.type === "openDetail" || a.type === "sequence";
        const navs = actions.filter(isNav);
        const rest = actions.filter((a) => !isNav(a));
        for (const a of navs) await runSafe(a);
        for (const a of rest) await runSafe(a);
      };

      try {
        for await (const ev of streamCopilotChat({
          token: loginToken,
          role: apiRole,
          sessionId: sessionIdRef.current,
          messages: [{ role: "user", content: modelText }],
          // Fresh each send: a text snapshot (eyes) + a scan of the actionable
          // controls on screen right now (hands). Refs in `controls` are only
          // valid for this message.
          pageContext: {
            ...pageContext,
            // A pinned document (eye button) forces its quote as the context,
            // so analyzeAttachment resolves it via ctx even if the model
            // forgets to pass quoteId through — the "open the relevant quote /
            // doc not selected" failure came from relying on the model for it.
            quoteId: askDoc?.quoteId || pageContext.quoteId,
            // The pinned doc's own conversation thread (airline/transport
            // threads differ from the customer thread the tools default to).
            attachThreadId: askDoc?.threadId || undefined,
            snapshot: capturePageSnapshot(),
            controls: scanActionable(),
          },
          signal: ac.signal,
        })) {
          if (ev.type === "token" && ev.text) {
            assistant += ev.text;
            setMessages((m) => {
              const n = [...m];
              const last = n.length - 1;
              n[last] = {
                ...n[last],
                text: assistant,
                tools: n[last].tools || [],
              };
              return n;
            });
          }
          if (ev.type === "tool_call" && ev.name) {
            setMessages((m) => {
              const n = [...m];
              const last = n.length - 1;
              const tools = [
                ...(n[last].tools || []),
                { name: ev.name, pending: true, ok: null },
              ];
              n[last] = { ...n[last], tools };
              return n;
            });
          }
          if (ev.type === "tool_result" && ev.name) {
            setMessages((m) => {
              const n = [...m];
              const last = n.length - 1;
              const tools = [...(n[last].tools || [])];
              const i = tools.findIndex((x) => x.name === ev.name && x.pending);
              if (i >= 0) {
                tools[i] = { name: ev.name, pending: false, ok: ev.ok };
              }
              n[last] = { ...n[last], tools };
              return n;
            });
          }
          if (ev.type === "client_action" && ev.action) {
            const act = ev.action;
            // Attachments card: not an action to perform — attach it to the
            // streaming assistant message so it renders as tappable rows.
            if (act.type === "attachments") {
              setMessages((m) => {
                if (!m.length) return m;
                const n = [...m];
                const last = n.length - 1;
                n[last] = {
                  ...n[last],
                  attachCard: { quoteId: act.quoteId, files: act.attachments || [] },
                };
                return n;
              });
              continue;
            }
            // Dispatch page changes the INSTANT they arrive so they overlap with
            // the model still streaming its follow-up actions — the new view is
            // already mounting/settling by the time the acts land. sequence runs
            // its own steps in order (nav → settle → acts). Everything else is
            // buffered and runs after the stream, once the navs have settled.
            if (
              act.type === "navigate" ||
              act.type === "openDetail" ||
              act.type === "sequence"
            ) {
              earlyNav.push(runSafe(act)); // track the promise; awaited at the tail
            } else {
              pendingActions.push(act);
            }
          }
          if (ev.type === "done" && ev.tokensUsed != null) {
            usage = ev.tokensUsed;
          }
          if (ev.type === "error" && ev.message) {
            assistant = String(ev.message);
            const b = (ev.message || "").toLowerCase();
            if (b.includes("budget") || b.includes("cap") || b.includes("tenant")) {
              assistant = "AI budget reached for today. Try again tomorrow.";
            }
            setMessages((m) => {
              const n = [...m];
              const last = n.length - 1;
              n[last] = { ...n[last], text: assistant, tools: n[last].tools || [] };
              return n;
            });
          }
        }
        // Wait for the mid-stream navigations/sequences to finish settling, THEN
        // run the buffered follow-ups against the now-current view.
        if (earlyNav.length) await Promise.all(earlyNav);
        if (pendingActions.length) await executeActions(pendingActions);

        // MOVE 2 — observe → act → verify → retry. If a driving action failed
        // and we haven't spent this turn's single retry, queue ONE hidden
        // follow-up asking the model to re-check the controls and try another
        // way. The budget flag is set NOW (not on flush) so nothing can race a
        // second retry in. autoRetryMsgRef flushes once `loading` clears.
        if (
          !isAutoRetry &&
          !autoRetryUsedRef.current &&
          drivingFailures.length > 0
        ) {
          autoRetryUsedRef.current = true;
          const firstFail = drivingFailures[0];
          autoRetryMsgRef.current = `[auto-retry] The previous step failed: "${firstFail}". Re-check the controls currently on screen and try a different approach.`;
        }
      } catch (e) {
        const st = e?.status;
        const msg =
          st === 429
            ? "Slow down — try again in a minute."
            : e?.name === "AbortError"
              ? ""
              : e?.message?.includes("fetch")
                ? "Connection lost. Type your message again."
                : e?.message || "Connection lost. Type your message again.";
        if (msg) {
          setMessages((m) => {
            const n = [...m];
            const last = n.length - 1;
            n[last] = {
              ...n[last],
              text: msg,
              tools: n[last].tools || [],
              streaming: false,
            };
            return n;
          });
        }
      } finally {
        setLastUsage(usage);
        setLoading(false);
        setMessages((m) => {
          const n = [...m];
          if (n.length) {
            const last = n.length - 1;
            const prev = n[last];
            const fixedText =
              prev.from === "assistant" && !prev.text
                ? "Connection lost — type your message again."
                : prev.text;
            n[last] = { ...prev, streaming: false, text: fixedText };
          }
          return n;
        });
        if (inputRef.current) {
          try {
            inputRef.current.focus();
          } catch {
            /* */
          }
        }
      }
    },
    [input, loginToken, role, loading, pageContext, navigate, askDoc]
  );

  // Append a result chip to the latest assistant message.
  /* Open the panel already asking a question.
     Used by the notification bell: a Copilot system alert literally tells the
     operator to "open the copilot and say ...", and clicking that tile used to
     navigate to /newQuotePage/undefined because system alerts carry no quote.
     Now the tile does the thing the message asked for. */
  useEffect(() => {
    const onAsk = (e) => {
      const prompt = String(e?.detail?.prompt || "").trim();
      if (!prompt) return;
      setOpen(true);
      // Let the panel mount before the turn starts, so the streamed answer
      // renders into a visible thread rather than a closed one.
      setTimeout(() => sendMessage(prompt), 0);
    };
    window.addEventListener("fk:copilot-ask", onAsk);
    return () => window.removeEventListener("fk:copilot-ask", onAsk);
  }, [sendMessage]);

  const appendActionChip = useCallback((chip) => {
    setMessages((m) => {
      if (!m.length) return m;
      const n = [...m];
      const last = n.length - 1;
      n[last] = { ...n[last], actions: [...(n[last].actions || []), chip] };
      return n;
    });
  }, []);

  // Preview an email attachment in a new tab. The tab is opened SYNCHRONOUSLY
  // (popup blockers only allow window.open inside the click), then pointed at
  // the authenticated blob once it arrives — the browser's native PDF viewer
  // does the rest.
  const previewAttachment = useCallback(
    async (card, file) => {
      const apiRole = role === "superAdmin" ? "admin" : role;
      const tab = window.open("", "_blank");
      try {
        if (tab) {
          tab.document.title = file.filename;
          tab.document.body.innerHTML =
            '<p style="font-family:sans-serif;color:#475569;padding:24px">Loading preview…</p>';
        }
      } catch {
        /* cross-origin quirks — the blob nav below still works */
      }
      const blob = await fetchCopilotAttachment({
        token: loginToken,
        role: apiRole,
        quoteId: card.quoteId,
        index: file.index,
        filename: file.filename,
      });
      if (!blob) {
        if (tab) tab.close();
        appendActionChip({ kind: "error", label: `couldn't open ${file.filename}` });
        return;
      }
      const url = URL.createObjectURL(blob);
      if (tab) {
        tab.location.href = url;
      } else {
        window.open(url, "_blank");
      }
      appendActionChip({ kind: "done", label: `Opened ${file.filename}` });
      // Revoke late: the tab needs the URL alive while it renders.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    [loginToken, role, appendActionChip]
  );

  // Local-document analysis (drag & drop / paperclip): works from ANY page —
  // the file goes straight to the analyze endpoint, no quote or thread needed.
  // Whatever is typed in the input box becomes the question about the document.
  const [dragOver, setDragOver] = useState(false);
  const [docBusy, setDocBusy] = useState(false);
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const dragHasFiles = (e) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");

  const analyzeLocalFile = useCallback(
    async (file) => {
      if (!file || docBusy || !canUse) return;
      const okType = /^(application\/pdf|image\/(png|jpe?g|webp|gif))$/i.test(
        file.type || ""
      );
      if (!okType) {
        setVoiceError("I can analyze PDFs and images only.");
        setTimeout(() => setVoiceError(""), 8000);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setVoiceError("That file is over 10 MB — too large to analyze.");
        setTimeout(() => setVoiceError(""), 8000);
        return;
      }
      const question = input.trim();
      if (question) setInput("");
      setDocBusy(true);
      setMessages((m) => [
        ...m,
        {
          from: "user",
          text: `📄 ${file.name}${question ? ` — ${question}` : ""}`,
          tools: [],
        },
        { from: "assistant", text: "", streaming: true, tools: [] },
      ]);
      const apiRole = role === "superAdmin" ? "admin" : role;
      const res = await analyzeCopilotUpload({
        token: loginToken,
        role: apiRole,
        file,
        question,
      });
      setMessages((m) => {
        const n = [...m];
        const last = n.length - 1;
        n[last] = {
          ...n[last],
          streaming: false,
          text: res?.analysis || "",
          actions: res?.analysis
            ? [{ kind: "done", label: `Analyzed ${file.name}` }]
            : [
                {
                  kind: "error",
                  label: res?.error || "couldn't analyze that document",
                },
              ],
        };
        return n;
      });
      setDocBusy(false);
    },
    [docBusy, canUse, input, loginToken, role]
  );

  // Execute a confirmed write through the normal authenticated API (never the
  // model). Registry keeps each write's real call in one obvious place.
  const executeConfirm = useCallback(
    async (pending) => {
      if (!pending) return;
      const { intent, params } = pending;
      // Copilot API routes map superAdmin → admin, same as the chat stream.
      const apiRole = role === "superAdmin" ? "admin" : role;
      setConfirmBusy(true);
      try {
        if (intent === "addNote") {
          const res = await upsertStickyNote({
            token: loginToken,
            role,
            id: params.quoteId,
            data: { content: params.content },
          });
          if (res?.error) throw new Error(res.error);
          appendActionChip({ kind: "done", label: "Note added to the quote" });
        } else if (intent === "sendReply") {
          // Send the drafted reply through the real authenticated endpoint,
          // using the operator's EDITED draft if they tweaked it. A 200
          // { ok:false } is a soft failure (e.g. no email thread) — surface the
          // reason and DON'T claim success.
          const finalBody = (replyDraft || params.body || "").trim();
          if (!finalBody) {
            appendActionChip({ kind: "error", label: "the reply is empty" });
            setConfirmBusy(false);
            return;
          }
          const res = await copilotReply({
            token: loginToken,
            role: apiRole,
            quoteId: params.quoteId,
            body: finalBody,
            cc: params.cc || [],
            bcc: params.bcc || [],
          });
          if (res?.ok) {
            appendActionChip({ kind: "done", label: "Reply sent to the customer" });
            // Optimistic update: tell the open conversation view to show the
            // sent reply immediately (no manual refresh). CustomerConversation /
            // the quote-detail thread listen for this and refetch/append.
            try {
              window.dispatchEvent(
                new CustomEvent("fk:copilot-reply-sent", {
                  detail: { quoteId: params.quoteId, body: finalBody },
                })
              );
            } catch {
              /* CustomEvent unsupported — the operator can still refresh */
            }
          } else {
            appendActionChip({
              kind: "error",
              label:
                res?.error ||
                "couldn't send the reply — reply from the conversation view",
            });
            // Not a success: don't fire the flourish. Return early past it.
            setConfirmBusy(false);
            setPendingConfirm(null);
            return;
          }
        } else if (intent === "requestRates") {
          // Fire rate requests to the confirmed carriers, then summarize which
          // ones actually went out vs. failed.
          const res = await copilotRequestRates({
            token: loginToken,
            role: apiRole,
            quoteId: params.quoteId,
            carriers: params.carriers || [],
            note: params.note,
          });
          const results = Array.isArray(res?.results) ? res.results : [];
          const sent = results.filter((r) => r?.ok);
          const failed = results.filter((r) => r && !r.ok);
          if (res?.ok === false && !results.length) {
            appendActionChip({
              kind: "error",
              label: res?.error || "couldn't send the rate requests",
            });
            setConfirmBusy(false);
            setPendingConfirm(null);
            return;
          }
          if (sent.length) {
            appendActionChip({
              kind: "done",
              label: `Rate requests sent to ${sent.length} ${
                sent.length === 1 ? "carrier" : "carriers"
              }`,
            });
          }
          if (failed.length) {
            const names = failed
              .map((r) => r.carrier || "a carrier")
              .join(", ");
            appendActionChip({
              kind: "error",
              label: `Couldn't reach: ${names}`,
            });
          }
          if (!sent.length && !failed.length) {
            appendActionChip({
              kind: "error",
              label: "no carriers were sent a rate request",
            });
            setConfirmBusy(false);
            setPendingConfirm(null);
            return;
          }
        } else if (intent === "bulkChase") {
          // One confirmed follow-up to many customers. Uses the operator's
          // EDITED draft; summarizes sent vs failed per the backend's per-quote
          // results (partial success is normal here).
          const finalBody = (replyDraft || params.body || "").trim();
          if (!finalBody) {
            appendActionChip({ kind: "error", label: "the follow-up is empty" });
            setConfirmBusy(false);
            return;
          }
          const res = await copilotBulkChase({
            token: loginToken,
            role: apiRole,
            items: params.items || [],
            body: finalBody,
          });
          if (res?.ok === false) {
            appendActionChip({
              kind: "error",
              label: res?.error || "couldn't send the follow-ups",
            });
            setConfirmBusy(false);
            setPendingConfirm(null);
            return;
          }
          if (res?.sent) {
            appendActionChip({
              kind: "done",
              label: `Follow-up sent to ${res.sent} customer${
                res.sent === 1 ? "" : "s"
              }`,
            });
          }
          if (res?.failed) {
            const names = (res.results || [])
              .filter((r) => r && !r.ok)
              .map((r) => r.vantRef || "a quote")
              .join(", ");
            appendActionChip({
              kind: "error",
              label: `Couldn't send for: ${names || `${res.failed} quotes`}`,
            });
          }
          if (!res?.sent && !res?.failed) {
            appendActionChip({
              kind: "error",
              label: "no follow-ups were sent",
            });
            setConfirmBusy(false);
            setPendingConfirm(null);
            return;
          }
        } else if (intent === "bulkStage") {
          // Confirmed stage move across many quotes.
          const res = await copilotBulkStage({
            token: loginToken,
            role: apiRole,
            items: params.items || [],
            newStage: params.newStage,
          });
          if (res?.ok === false) {
            appendActionChip({
              kind: "error",
              label: res?.error || "couldn't move the quotes",
            });
            setConfirmBusy(false);
            setPendingConfirm(null);
            return;
          }
          if (res?.updated) {
            appendActionChip({
              kind: "done",
              label: `Moved ${res.updated} quote${
                res.updated === 1 ? "" : "s"
              } to "${params.newStage}"`,
            });
          }
          if (res?.failed) {
            const names = (res.results || [])
              .filter((r) => r && !r.ok)
              .map((r) => r.vantRef || "a quote")
              .join(", ");
            appendActionChip({
              kind: "error",
              label: `Couldn't move: ${names || `${res.failed} quotes`}`,
            });
          }
          if (!res?.updated && !res?.failed) {
            appendActionChip({
              kind: "error",
              label: "no quotes were moved",
            });
            setConfirmBusy(false);
            setPendingConfirm(null);
            return;
          }
        } else if (intent === "replayControl") {
          // Operator approved a commit (send/save/submit/mutating select) —
          // replay it, bypassing the guard.
          const r =
            pending.kind === "set"
              ? await forceSet(pending.ref, pending.value)
              : forceClick(pending.ref);
          appendActionChip({
            kind: r.ok ? "done" : "error",
            label: r.ok ? `Done: ${r.label}` : r.label,
          });
          if (!r.ok) throw new Error(r.label);
        } else {
          throw new Error("Unknown action");
        }
        setFlourish((f) => f + 1);
        setBurstKey((b) => b + 1);
      } catch (e) {
        appendActionChip({ kind: "error", label: "Couldn't complete that" });
      } finally {
        setConfirmBusy(false);
        setPendingConfirm(null);
      }
    },
    [loginToken, role, appendActionChip, replyDraft]
  );
  /* copilotReply / copilotRequestRates are module-level imports (stable), so
   * they don't belong in the dep array; loginToken/role cover the auth inputs. */

  // Voice command routing: approve/cancel a pending confirm by voice, otherwise
  // send — queuing the transcript if a reply is still streaming so a spoken
  // command is never silently dropped (hands-free reliability).
  const pendingSendRef = useRef("");
  const handleVoiceTranscript = useCallback(
    (text) => {
      const t = String(text || "").trim();
      if (!t) return;
      if (pendingConfirm) {
        if (/^\s*(yes|yeah|yep|confirm|ok(ay)?|do it|go ahead|save|approve|sure)\b/i.test(t)) {
          if (!confirmBusy) executeConfirm(pendingConfirm);
          return;
        }
        if (/^\s*(no|nope|cancel|stop|don'?t|dismiss|never mind)\b/i.test(t)) {
          setPendingConfirm(null);
          return;
        }
      }
      voiceTurnRef.current = true; // this turn came from voice → confirm re-arms
      if (loading) {
        pendingSendRef.current = t; // flush when the current reply finishes
        setQueuedPreview(t);
        return;
      }
      sendMessage(t);
    },
    [pendingConfirm, confirmBusy, executeConfirm, loading, sendMessage]
  );
  // Flush a queued command (voice or typed) once streaming ends.
  useEffect(() => {
    if (!loading && pendingSendRef.current) {
      const t = pendingSendRef.current;
      pendingSendRef.current = "";
      setQueuedPreview("");
      // A real queued command supersedes a pending auto-retry — the operator's
      // fresh intent wins, and it'll reset the retry budget on its own.
      autoRetryMsgRef.current = "";
      sendMessage(t);
    }
  }, [loading, sendMessage]);
  // Flush a queued auto-retry once streaming ends (only if no real user command
  // is waiting). Marked isAutoRetry so it renders as a subtle system line and
  // does NOT reset the one-per-turn retry budget.
  useEffect(() => {
    if (!loading && !pendingSendRef.current && autoRetryMsgRef.current) {
      const t = autoRetryMsgRef.current;
      autoRetryMsgRef.current = "";
      sendMessage(t, { isAutoRetry: true });
    }
  }, [loading, sendMessage]);
  // Re-arm the mic when a voice turn raises a confirm, so "yes"/"cancel" works
  // hands-free. Typed/chip turns leave the mic idle.
  useEffect(() => {
    if (pendingConfirm && voiceTurnRef.current) setVoiceArm((v) => v + 1);
  }, [pendingConfirm]);

  const startNewChat = useCallback(() => {
    const sid =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : String(Date.now());
    sessionIdRef.current = sid;
    try {
      window.sessionStorage?.setItem(SESSION_KEY, sid);
    } catch {
      /* */
    }
    setMessages([{ from: "assistant", text: greeting, tools: [] }]);
    setLastUsage(null);
    setHistoryOpen(false);
    setQueuedPreview("");
  }, [greeting]);

  // Dismiss any pending confirm when the panel closes.
  useEffect(() => {
    if (!open) setPendingConfirm(null);
  }, [open]);

  const apiRoleForCalls = role === "superAdmin" ? "admin" : role;

  // MOVE 3 — proactive nudges. While the panel is CLOSED and we're on a quote,
  // debounce-fetch nudges. If any come back (and we haven't nudged this quote
  // yet this session), stash the first and pulse the FAB (reuse the flourish
  // mechanism). We never auto-open the panel and never re-fetch a quote already
  // seen. When the operator opens the panel, the stashed nudge surfaces below.
  useEffect(() => {
    const quoteId = pageContext.quoteId;
    if (open) return undefined; // only prospect while closed
    if (!quoteId || !loginToken || !canUse) return undefined;
    if (nudgeSeenRef.current.has(quoteId)) return undefined;
    let cancelled = false;
    const ac = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const { nudges } = await copilotNudges({
          token: loginToken,
          role: apiRoleForCalls,
          quoteId,
          signal: ac.signal,
        });
        if (cancelled || !nudges.length) return;
        // Mark seen the moment we decide to surface it — quiet, once per quote.
        nudgeSeenRef.current.add(quoteId);
        setStashedNudge({ ...nudges[0], quoteId });
        setFlourish((f) => f + 1); // subtle FAB pulse
      } catch {
        /* nudges are best-effort; stay quiet on failure */
      }
    }, 1200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      try {
        ac.abort();
      } catch {
        /* */
      }
    };
  }, [open, pageContext.quoteId, loginToken, apiRoleForCalls, canUse]);

  // Drop a stashed nudge if the operator navigates off its quote — it's no
  // longer relevant to the view (never surface a stale suggestion).
  useEffect(() => {
    if (stashedNudge && stashedNudge.quoteId !== pageContext.quoteId) {
      setStashedNudge(null);
    }
  }, [pageContext.quoteId, stashedNudge]);

  const refreshSessions = useCallback(async () => {
    if (!loginToken || !apiRoleForCalls) return;
    setSessionsLoading(true);
    try {
      const data = await listCopilotSessions({
        token: loginToken,
        role: apiRoleForCalls,
      });
      setSessions(Array.isArray(data?.sessions) ? data.sessions : []);
    } catch (err) {
      console.warn("Copilot session list failed:", err?.message);
    } finally {
      setSessionsLoading(false);
    }
  }, [loginToken, apiRoleForCalls]);

  useEffect(() => {
    if (open && historyOpen) refreshSessions();
  }, [open, historyOpen, refreshSessions]);

  const loadSession = useCallback(
    async (sessionId) => {
      if (!sessionId || !loginToken || !apiRoleForCalls) return;
      try {
        const data = await getCopilotSession({
          token: loginToken,
          role: apiRoleForCalls,
          sessionId,
        });
        const msgs = Array.isArray(data?.messages) ? data.messages : [];
        sessionIdRef.current = sessionId;
        if (typeof window !== "undefined" && window.sessionStorage) {
          window.sessionStorage.setItem(SESSION_KEY, sessionId);
        }
        const restored = msgs.map((m) => ({
          from: m.role === "user" ? "user" : "assistant",
          text: String(m.content || ""),
          tools: [],
        }));
        setMessages(
          restored.length
            ? restored
            : [{ from: "assistant", text: greeting, tools: [] }]
        );
        setHistoryOpen(false);
      } catch (err) {
        console.warn("Failed to load session:", err?.message);
      }
    },
    [loginToken, apiRoleForCalls, greeting]
  );

  function handleInputKey(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const t = input.trim();
      if (!t) return;
      voiceTurnRef.current = false; // typed turn
      if (loading) {
        // Queue instead of dropping — flushes when the current reply ends.
        pendingSendRef.current = t;
        setQueuedPreview(t);
        setInput("");
        return;
      }
      sendMessage();
    }
  }
  function handleSendClick() {
    const t = input.trim();
    if (!t) return;
    voiceTurnRef.current = false;
    if (loading) {
      pendingSendRef.current = t;
      setQueuedPreview(t);
      setInput("");
      return;
    }
    sendMessage();
  }

  const overlayRoot = typeof document !== "undefined" ? document.body : null;

  const conversationStarted = messages.length > 1;

  return (
    <>
      {overlayRoot &&
        createPortal(
          <div data-copilot-root>
            <AnimatePresence initial={false}>
              {!open && (
                <motion.button
                  key="fab"
                  ref={fabRef}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={{ type: "spring", stiffness: 420, damping: 26 }}
                  whileHover={{ scale: 1.08 }}
                  whileTap={{ scale: 0.94 }}
                  /* DOCKED BOTTOM-RIGHT, not centred on the right edge. Floating
                     at top-1/2 put it on top of whatever happened to be at the
                     vertical middle of the page — "the floating mic FAB overlaps
                     content repeatedly (it hid the Apply to form button and
                     covered inspector fields)" — and it forced every wide layout
                     to reserve an empty lane down the whole right side to avoid
                     it. The bottom-right corner is the one place nothing is laid
                     out, and it is where the Notes button already lives, so the
                     two now stack instead of one squatting mid-page.

                     bottom-28 rather than bottom-24: the aura halo is inset -45%
                     on a 56px button, so it bleeds ~25px past the edge and would
                     otherwise graze the Notes pill. */
                  className={`group fixed bottom-28 right-6 z-[10000] flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-fk-accent shadow-[0_8px_24px_rgba(99,60,220,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 dark:from-violet-500 dark:to-blue-600 ${
                    stashedNudge
                      ? "ring-2 ring-violet-300/70 ring-offset-2 ring-offset-transparent"
                      : ""
                  }`}
                  title={
                    stashedNudge
                      ? "FreightKit Copilot — I have a suggestion (Alt+M for voice)"
                      : "FreightKit Copilot (Alt+M for voice)"
                  }
                  aria-label={
                    stashedNudge
                      ? "Open Copilot — a suggestion is waiting"
                      : "Open Copilot"
                  }
                  onClick={() => openWithVoice("fab")}
                >
                  {/* Living purple aura (design inspiration): a soft halo breathes
                      and rings ripple outward beyond the orb. */}
                  <span className="fk-aura-halo pointer-events-none absolute -z-10" style={{ inset: "-45%", borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246,0.55), rgba(124,58,237,0.18) 55%, transparent 72%)", filter: "blur(6px)" }} aria-hidden />
                  <span className="fk-aura-ring pointer-events-none absolute -z-10" style={{ inset: "-8%", borderRadius: "50%", border: "1.5px solid rgba(139,92,246,0.5)" }} aria-hidden />
                  <span className="fk-aura-ring pointer-events-none absolute -z-10" style={{ inset: "-8%", borderRadius: "50%", border: "1.5px solid rgba(139,92,246,0.5)", animationDelay: "1.4s" }} aria-hidden />
                  {/* The living orb IS the button face; mic sits on top. */}
                  <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-full opacity-90" aria-hidden>
                    <AnimatedLoader variant="orb" size={56} label="" />
                  </span>
                  <FontAwesomeIcon icon={faMicrophone} className="relative z-10 text-lg text-white" />
                  {/* Quiet pulse dot when a nudge is waiting — never a badge count. */}
                  {stashedNudge && (
                    <span
                      className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-white bg-violet-400 motion-safe:animate-pulse dark:border-[oklch(0.26_0.02_264)]"
                      aria-hidden
                    />
                  )}
                </motion.button>
              )}

              {open && (
                <motion.div
                  key="panel"
                  role="dialog"
                  aria-label="FreightKit Copilot"
                  initial={{ x: "100%" }}
                  animate={{ x: 0, transition: { duration: 0.26, ease: [0.22, 1, 0.36, 1] } }}
                  exit={{ x: "100%", transition: { duration: 0.18, ease: "easeIn" } }}
                  className={`fixed right-0 top-0 z-[10001] flex h-screen w-full min-h-0 max-w-full flex-col border-l border-slate-200 bg-white shadow-[-12px_0_40px_rgba(15,23,42,0.12)] dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.168_0.021_264)] sm:w-[400px] ${
                    flourishOn ? "fk-action-glow" : ""
                  }`}
                  onDragEnter={(e) => {
                    if (!dragHasFiles(e)) return;
                    e.preventDefault();
                    dragDepthRef.current += 1;
                    setDragOver(true);
                  }}
                  onDragOver={(e) => {
                    if (dragHasFiles(e)) e.preventDefault();
                  }}
                  onDragLeave={() => {
                    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
                    if (!dragDepthRef.current) setDragOver(false);
                  }}
                  onDrop={(e) => {
                    if (!dragHasFiles(e)) return;
                    e.preventDefault();
                    dragDepthRef.current = 0;
                    setDragOver(false);
                    const f = e.dataTransfer.files?.[0];
                    if (f) analyzeLocalFile(f);
                  }}
                >
                  {dragOver && (
                    <div
                      className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-violet-600/10 backdrop-blur-[1px]"
                      aria-hidden
                    >
                      <div className="rounded-2xl border-2 border-dashed border-violet-400 bg-white/95 px-6 py-4 text-center shadow-lg dark:border-violet-500/60 dark:bg-[oklch(0.225_0.026_264/0.95)]">
                        <FontAwesomeIcon icon={faFileLines} className="text-xl text-violet-500" />
                        <p className="mt-1 text-sm font-semibold text-violet-700 dark:text-violet-300">
                          Drop to analyze
                        </p>
                        <p className="text-[11px] text-slate-500 dark:text-slate-400">
                          PDF or image · up to 10 MB
                        </p>
                      </div>
                    </div>
                  )}
                  {/* One-shot success burst after a confirmed write. */}
                  <AnimatePresence>
                    {burstKey > 0 && (
                      <motion.div
                        key={burstKey}
                        initial={{ opacity: 0, scale: 0.85 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 1.05 }}
                        transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                        className="pointer-events-none absolute inset-0 z-30 flex items-center justify-center"
                        aria-hidden
                      >
                        <AnimatedLoader variant="success" size={180} label="Done" />
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <header className="flex shrink-0 items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-[oklch(0.3_0.02_264)]">
                    <div className="flex min-w-0 items-center gap-2.5">
                      <span className="relative inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-fk-accent shadow-sm dark:from-violet-500 dark:to-blue-600">
                        <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-full opacity-90" aria-hidden>
                          <AnimatedLoader variant="orb" size={36} label="" />
                        </span>
                        <FontAwesomeIcon icon={faMicrophone} className="relative z-10 text-[13px] text-white" />
                        {voiceStatus === "recording" && (
                          <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-red-500 motion-safe:animate-pulse dark:border-[oklch(0.26_0.02_264)]" aria-hidden />
                        )}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold leading-tight tracking-[-0.01em] text-slate-800 dark:text-slate-100">
                          FreightKit Copilot
                        </p>
                        <p
                          className="truncate text-[11px] leading-tight text-slate-500 dark:text-slate-400"
                          role="status"
                          title={lastUsage > 0 ? `${lastUsage.toLocaleString()} tokens this chat` : undefined}
                        >
                          {voiceStatus === "recording" ? (
                            <span className="font-medium text-red-500">Listening…</span>
                          ) : voiceStatus === "transcribing" ? (
                            "Transcribing…"
                          ) : loading ? (
                            "Thinking…"
                          ) : (
                            "Voice-ready · Alt+M"
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-0.5">
                      {conversationStarted && (
                        <button
                          type="button"
                          onClick={startNewChat}
                          title="New chat"
                          aria-label="New chat"
                          className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-[oklch(0.26_0.028_264)]"
                        >
                          <FontAwesomeIcon icon={faPlus} className="text-[12px]" />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => setHistoryOpen((v) => !v)}
                        title="Recent conversations"
                        aria-label="Recent conversations"
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors ${
                          historyOpen
                            ? "bg-violet-50 text-violet-600 dark:bg-violet-900/30 dark:text-violet-300"
                            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-[oklch(0.26_0.028_264)]"
                        }`}
                      >
                        <FontAwesomeIcon icon={faClockRotateLeft} className="text-[12px]" />
                      </button>
                      <button
                        type="button"
                        aria-label="Close chat"
                        title="Close (Esc)"
                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-[oklch(0.26_0.028_264)]"
                        onClick={closePanel}
                      >
                        <FontAwesomeIcon icon={faXmark} className="text-[13px]" />
                      </button>
                    </div>
                  </header>

                  <AnimatePresence initial={false}>
                    {historyOpen && (
                      <motion.div
                        key="history"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="shrink-0 overflow-hidden border-b border-slate-200 bg-white dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.168_0.021_264)]"
                      >
                        <div className="px-3 py-2">
                          <div className="mb-1.5 flex items-center justify-between text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                            <span>Recent chats</span>
                            <button
                              type="button"
                              onClick={refreshSessions}
                              className="inline-flex items-center gap-1 text-violet-600 hover:underline disabled:opacity-50 dark:text-violet-300"
                              disabled={sessionsLoading}
                            >
                              {sessionsLoading ? (
                                <FontAwesomeIcon icon={faCircleNotch} className="animate-spin text-[10px]" />
                              ) : (
                                "Refresh"
                              )}
                            </button>
                          </div>
                          {sessionsLoading && sessions.length === 0 ? (
                            <div className="space-y-1">
                              <div className="h-11 rounded-xl bg-slate-100 motion-safe:animate-pulse dark:bg-[oklch(0.26_0.028_264/0.8)]" />
                              <div className="h-11 rounded-xl bg-slate-100 motion-safe:animate-pulse dark:bg-[oklch(0.26_0.028_264/0.8)]" />
                            </div>
                          ) : sessions.length === 0 ? (
                            <div className="flex flex-col items-center gap-1.5 py-4 text-center">
                              <FontAwesomeIcon icon={faClockRotateLeft} className="text-lg text-slate-300 dark:text-slate-600" />
                              <p className="text-xs text-slate-500 dark:text-slate-400">
                                No saved chats yet.
                              </p>
                            </div>
                          ) : (
                            <ul className="max-h-44 space-y-1 overflow-y-auto pr-1">
                              {sessions.map((s) => {
                                const isCurrent = s.sessionId === sessionIdRef.current;
                                const mins =
                                  s.expiresInSec != null
                                    ? Math.max(1, Math.round(s.expiresInSec / 60))
                                    : null;
                                return (
                                  <li key={s.sessionId}>
                                    <button
                                      type="button"
                                      onClick={() => loadSession(s.sessionId)}
                                      className={`w-full rounded-xl border px-2.5 py-1.5 text-left text-xs transition ${
                                        isCurrent
                                          ? "border-violet-300 bg-violet-50 text-violet-900 dark:border-violet-500/40 dark:bg-violet-900/30 dark:text-violet-100"
                                          : "border-slate-200 bg-white hover:bg-slate-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:hover:bg-[oklch(0.3_0.024_264/0.6)]"
                                      }`}
                                    >
                                      <div className="truncate font-medium text-slate-700 dark:text-slate-200">
                                        {s.lastUserPreview || "(empty)"}
                                      </div>
                                      <div className="mt-0.5 flex items-center justify-between text-[10px] text-slate-500 dark:text-slate-400">
                                        <span>{s.messageCount} msgs</span>
                                        {mins != null && (
                                          <span title={`Auto-clears in ~${mins}m`}>
                                            {isCurrent ? "current" : `~${mins}m`}
                                          </span>
                                        )}
                                      </div>
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div
                    ref={listRef}
                    onScroll={onListScroll}
                    className="relative min-h-0 flex-1 overflow-y-auto bg-slate-50 bg-[radial-gradient(120%_55%_at_50%_0%,rgba(139,92,246,0.05),transparent_60%)] px-4 py-3 dark:bg-[oklch(0.225_0.026_264)]"
                    aria-live="polite"
                  >
                    {!conversationStarted ? (
                      <motion.div
                        variants={staggerContainer(0.05)}
                        initial="hidden"
                        animate="show"
                        className="flex h-full flex-col items-center justify-center gap-3 px-2 text-center"
                      >
                        <motion.div
                          variants={staggerItem}
                          className="relative flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 to-fk-accent shadow-[0_8px_24px_rgba(99,60,220,0.25)] dark:from-violet-500 dark:to-blue-600"
                        >
                          <span className="pointer-events-none absolute inset-0" aria-hidden>
                            <AnimatedLoader variant="orb" size={56} label="" />
                          </span>
                          <FontAwesomeIcon icon={faMicrophone} className="relative z-10 text-white" />
                        </motion.div>
                        <motion.p variants={staggerItem} className="text-sm font-semibold text-slate-700 dark:text-slate-200">
                          What can I get done?
                        </motion.p>
                        <motion.p variants={staggerItem} className="max-w-[16rem] text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                          I open pages, fill fields and click buttons for you — or just answer. Say it or type it.
                        </motion.p>
                        <motion.div variants={staggerItem} className="mt-1 grid w-full grid-cols-2 gap-2">
                          {quickActions.map((qa) => (
                            <button
                              key={qa.label}
                              type="button"
                              onClick={() => {
                                voiceTurnRef.current = false;
                                sendMessage(qa.text);
                              }}
                              disabled={loading || !canUse}
                              className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-2.5 py-2 text-left text-xs font-medium text-slate-700 shadow-sm transition-colors hover:border-violet-300 hover:bg-violet-50/40 disabled:opacity-50 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200 dark:hover:border-violet-500/50 dark:hover:bg-violet-900/20"
                            >
                              <FontAwesomeIcon icon={qa.icon} className="shrink-0 text-[11px] text-violet-500 dark:text-violet-300" />
                              <span className="truncate">{qa.label}</span>
                            </button>
                          ))}
                        </motion.div>
                      </motion.div>
                    ) : (
                      <>
                        {messages.map((msg, idx) => {
                          const hasActions = (msg.actions || []).length > 0;
                          // Auto-retry status: a quiet centered line, not a bubble.
                          if (msg.from === "system") {
                            return (
                              <motion.div
                                key={idx}
                                layout="position"
                                initial={{ opacity: 0, y: 6 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.2 }}
                                className="mb-3 flex justify-center"
                              >
                                <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-medium text-slate-500 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-400">
                                  <FontAwesomeIcon icon={faArrowsUpDown} className="text-[9px] text-violet-400" />
                                  {msg.text}
                                </span>
                              </motion.div>
                            );
                          }
                          return (
                            <motion.div
                              key={idx}
                              layout="position"
                              initial={{ opacity: 0, y: 8, scale: 0.98 }}
                              animate={{ opacity: 1, y: 0, scale: 1 }}
                              transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
                              className={`mb-3 flex ${msg.from === "user" ? "justify-end" : "justify-start"}`}
                            >
                              <div
                                className={`max-w-[85%] space-y-1.5 rounded-2xl px-3 py-2 text-sm shadow-sm ${
                                  msg.from === "user"
                                    ? "bg-fk-accent text-white dark:bg-blue-600"
                                    : `bg-white text-slate-800 ring-1 ring-slate-200/80 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-100 dark:ring-[oklch(0.33_0.022_264)]${
                                        msg.from === "assistant" && msg.streaming
                                          ? " fk-stream-ring ring-1 ring-violet-300/70 dark:ring-violet-600/70"
                                          : ""
                                      }`
                                }`}
                              >
                                <div className="whitespace-pre-wrap break-words">
                                  {msg.from === "assistant" && msg.streaming && !msg.text ? (
                                    <AnimatedLoader variant="orb" size={64} label="Thinking" />
                                  ) : (
                                    msg.text
                                  )}
                                </div>
                                {msg.attachCard && (
                                  <AttachmentCard
                                    card={msg.attachCard}
                                    disabled={loading}
                                    onAnalyze={(f) => {
                                      // Pin the doc so follow-up questions stay
                                      // routed to it (same as the eye button).
                                      setAskDoc({
                                        quoteId: msg.attachCard.quoteId || null,
                                        filename: String(f.filename),
                                        threadId: null,
                                      });
                                      sendMessage(
                                        `Analyze attachment ${f.index}: "${f.filename}"`
                                      );
                                    }}
                                    onPreview={(f) => previewAttachment(msg.attachCard, f)}
                                  />
                                )}
                                {/* Tool trace is the "working" signal; once the human
                                    action chips land they ARE the confirmation, so
                                    suppress the raw tool names to cut the clutter. */}
                                {!hasActions &&
                                  (msg.tools || []).map((t, i) => (
                                    <span
                                      key={i}
                                      title={t.name}
                                      style={{ animationDelay: `${i * 45}ms` }}
                                      className="fk-action-chip my-1 mr-1 inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600 dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-300"
                                    >
                                      {t.ok === true ? (
                                        <FontAwesomeIcon icon={faCheck} className="text-[9px] text-emerald-500" />
                                      ) : t.ok === false ? (
                                        <FontAwesomeIcon icon={faXmark} className="text-[9px] text-rose-500" />
                                      ) : (
                                        <span className="h-1 w-1 animate-pulse rounded-full bg-violet-500" />
                                      )}
                                      {humanTool(t.name)}
                                    </span>
                                  ))}
                                {(msg.actions || []).map((a, i) => {
                                  const tone =
                                    a.kind === "done"
                                      ? "bg-emerald-50 text-emerald-700 ring-emerald-200/70 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/30"
                                      : a.kind === "error"
                                        ? "bg-rose-50 text-rose-700 ring-rose-200/70 dark:bg-rose-500/10 dark:text-rose-300 dark:ring-rose-500/30"
                                        : "bg-blue-50 text-blue-700 ring-blue-200/70 dark:bg-blue-500/10 dark:text-blue-300 dark:ring-blue-500/30";
                                  return (
                                    <span
                                      key={`act-${i}`}
                                      style={{ animationDelay: `${i * 45}ms` }}
                                      className={`fk-action-chip my-1 mr-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${tone}`}
                                    >
                                      <FontAwesomeIcon icon={ACTION_ICONS[a.kind] || faWandMagicSparkles} className="text-[9px]" />
                                      {a.label}
                                    </span>
                                  );
                                })}
                              </div>
                            </motion.div>
                          );
                        })}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  <div className="shrink-0 space-y-2 border-t border-slate-200 bg-white px-3 py-3 dark:border-[oklch(0.3_0.02_264)] dark:bg-[oklch(0.168_0.021_264)]">
                    {/* Proactive nudge (MOVE 3): one quiet suggestion for the
                        current quote. Click sends the prompt; × dismisses. It's
                        already marked seen for this quote, so it never re-shows. */}
                    <AnimatePresence>
                      {stashedNudge &&
                        stashedNudge.quoteId === pageContext.quoteId && (
                          <motion.div
                            key="nudge"
                            variants={popIn}
                            initial="hidden"
                            animate="show"
                            exit="exit"
                            className="flex items-center gap-2 rounded-xl bg-violet-50/70 px-2.5 py-2 text-[11px] text-violet-800 ring-1 ring-violet-200/60 dark:bg-violet-900/20 dark:text-violet-200 dark:ring-violet-500/25"
                          >
                            <button
                              type="button"
                              disabled={loading || !canUse}
                              onClick={() => {
                                const prompt = stashedNudge.suggestedPrompt;
                                setStashedNudge(null);
                                voiceTurnRef.current = false;
                                if (prompt) sendMessage(prompt);
                              }}
                              className="flex min-w-0 flex-1 items-center gap-1.5 text-left font-medium hover:underline disabled:opacity-50"
                            >
                              <FontAwesomeIcon
                                icon={faWandMagicSparkles}
                                className="shrink-0 text-[11px] text-violet-500 dark:text-violet-300"
                              />
                              <span className="truncate">{stashedNudge.text}</span>
                            </button>
                            <button
                              type="button"
                              aria-label="Dismiss suggestion"
                              title="Dismiss"
                              onClick={() => setStashedNudge(null)}
                              className="shrink-0 rounded-full p-0.5 text-violet-500 hover:bg-violet-100 hover:text-violet-700 dark:hover:bg-violet-800/40"
                            >
                              <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
                            </button>
                          </motion.div>
                        )}
                    </AnimatePresence>

                    <AnimatePresence>
                      {showCoach && (
                        <motion.div
                          key="coach"
                          variants={popIn}
                          initial="hidden"
                          animate="show"
                          exit="exit"
                          className="flex items-start gap-2 rounded-xl bg-violet-50 p-2.5 text-[11px] text-violet-800 ring-1 ring-violet-200/70 dark:bg-violet-900/25 dark:text-violet-200 dark:ring-violet-500/30"
                        >
                          <FontAwesomeIcon icon={faMicrophone} className="mt-0.5 text-[11px]" />
                          {/* Shown once per operator, so it is what they believe
                              the copilot does. It promised the mic armed on open
                              long after that was removed — an experienced tester
                              filed a bug against copy, not behaviour. Keep it
                              true to openWithVoice(): only the hotkey/mic arms. */}
                          <span className="flex-1">
                            Type, or tap the mic to talk (Alt+M). While recording: pause to send, Esc to discard.
                          </span>
                          <button
                            type="button"
                            onClick={dismissCoach}
                            className="shrink-0 font-semibold text-violet-700 hover:underline dark:text-violet-200"
                          >
                            Got it
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <AnimatePresence>
                      {pendingConfirm && (
                        <motion.div
                          key="confirm"
                          variants={popIn}
                          initial="hidden"
                          animate="show"
                          exit="exit"
                          role="alertdialog"
                          aria-label="Confirm this change"
                          className="rounded-xl bg-white p-3 shadow-lg ring-1 ring-violet-400/40 dark:bg-[oklch(0.26_0.028_264)] dark:ring-violet-500/40"
                        >
                          <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">
                            {pendingConfirm.intent === "sendReply"
                              ? "Review & send reply"
                              : pendingConfirm.intent === "bulkChase"
                              ? "Review & send follow-ups"
                              : "Confirm this change"}
                          </p>
                          {pendingConfirm.intent === "sendReply" ||
                          pendingConfirm.intent === "bulkChase" ? (
                            <>
                              {/* The recipient line/list (everything before the
                                  body in the summary) stays as read-only context;
                                  the body is editable so the operator can tweak it. */}
                              <p className="mt-1 whitespace-pre-line text-xs text-slate-600 dark:text-slate-300">
                                {String(pendingConfirm.summary || "").split("\n\n")[0] ||
                                  "Send this reply?"}
                              </p>
                              <textarea
                                value={replyDraft}
                                onChange={(e) => setReplyDraft(e.target.value)}
                                disabled={confirmBusy}
                                rows={7}
                                aria-label="Reply message"
                                className="mt-1.5 max-h-56 w-full resize-y rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-xs leading-relaxed text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-fk-accent dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.225_0.026_264)] dark:text-slate-100"
                              />
                            </>
                          ) : (
                            <p className="mt-1 whitespace-pre-line text-xs text-slate-600 dark:text-slate-300">
                              {pendingConfirm.summary || "Apply this change?"}
                            </p>
                          )}
                          <div className="mt-2.5 flex items-center gap-2">
                            <button
                              type="button"
                              disabled={
                                confirmBusy ||
                                ((pendingConfirm.intent === "sendReply" ||
                                  pendingConfirm.intent === "bulkChase") &&
                                  !replyDraft.trim())
                              }
                              onClick={() => {
                                if (!confirmBusy) executeConfirm(pendingConfirm);
                              }}
                              className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-500 disabled:opacity-60"
                            >
                              {confirmBusy
                                ? pendingConfirm.intent === "sendReply" ||
                                  pendingConfirm.intent === "bulkChase"
                                  ? "Sending…"
                                  : "Saving…"
                                : pendingConfirm.confirmLabel || "Confirm"}
                            </button>
                            <button
                              type="button"
                              disabled={confirmBusy}
                              onClick={() => setPendingConfirm(null)}
                              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100 dark:border-[oklch(0.4_0.02_264)] dark:text-slate-300 dark:hover:bg-[oklch(0.3_0.024_264)]"
                            >
                              Cancel
                            </button>
                            <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-slate-400 dark:text-slate-500">
                              <FontAwesomeIcon icon={faMicrophone} className="text-[9px]" /> say “yes” or “cancel”
                            </span>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    {queuedPreview && loading && (
                      <p className="truncate text-center text-[11px] font-medium text-violet-600 dark:text-violet-300">
                        Queued: “{queuedPreview.slice(0, 52)}” — sends when this reply finishes
                      </p>
                    )}
                    {showPrivacy && (
                      <p className="flex items-center justify-center gap-1 text-center text-[10px] text-slate-500 dark:text-slate-500">
                        <FontAwesomeIcon icon={faLock} className="text-[9px]" />
                        This chat shares quote …{shortId} with the AI.
                      </p>
                    )}
                    {voiceError && (
                      <p className="text-center text-[11px] font-medium text-red-500" role="alert">
                        {voiceError}
                      </p>
                    )}
                    {askDoc && (
                      <div className="space-y-1.5">
                        <div className="flex items-center gap-2 rounded-xl bg-violet-50/70 px-2.5 py-2 text-[11px] ring-1 ring-violet-200/60 dark:bg-violet-900/20 dark:ring-violet-500/25">
                          <FontAwesomeIcon icon={faFileLines} className="shrink-0 text-[11px] text-violet-500" />
                          <span
                            className="min-w-0 flex-1 truncate font-medium text-violet-800 dark:text-violet-200"
                            title={askDoc.filename}
                          >
                            {askDoc.filename}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              previewAttachment(
                                { quoteId: askDoc.quoteId },
                                { filename: askDoc.filename }
                              )
                            }
                            className="shrink-0 font-semibold text-violet-700 underline-offset-2 hover:underline dark:text-violet-300"
                          >
                            Preview
                          </button>
                          {/* Analysis answers questions in chat; this writes the
                              shipment INTO the quote. For "cargo details
                              attached" enquiries the packing list is the only
                              place the dimensions exist, so reading it and
                              leaving the container empty solves nothing. */}
                          {askDoc.quoteId && (
                            <button
                              type="button"
                              onClick={() =>
                                window.dispatchEvent(
                                  new CustomEvent("fk:parse-attachment", {
                                    detail: {
                                      quoteId: askDoc.quoteId,
                                      filename: askDoc.filename,
                                      threadId: askDoc.threadId,
                                    },
                                  })
                                )
                              }
                              className="shrink-0 font-semibold text-violet-700 underline-offset-2 hover:underline dark:text-violet-300"
                            >
                              Fill quote fields
                            </button>
                          )}
                          <button
                            type="button"
                            aria-label="Unpin this document"
                            onClick={() => setAskDoc(null)}
                            className="shrink-0 rounded p-0.5 text-violet-400 transition hover:text-violet-700 dark:hover:text-violet-200"
                          >
                            <FontAwesomeIcon icon={faXmark} className="text-[11px]" />
                          </button>
                        </div>
                        {!loading && (
                          <div className="flex flex-wrap gap-1.5">
                            {["Summarize this", "Dangerous goods?", "Key details"].map((q) => (
                              <button
                                key={q}
                                type="button"
                                onClick={() => sendMessage(q)}
                                className="rounded-full border border-violet-200 bg-white px-2.5 py-1 text-[10px] font-medium text-violet-700 transition hover:bg-violet-50 dark:border-violet-500/30 dark:bg-[oklch(0.225_0.026_264)] dark:text-violet-300 dark:hover:bg-violet-900/25"
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                    <div
                      className={`flex items-center gap-2 rounded-full border bg-slate-50 p-1.5 transition-[box-shadow,border-color] duration-200 dark:bg-[oklch(0.225_0.026_264)] ${
                        voiceStatus === "recording"
                          ? "border-red-300 ring-2 ring-red-200/60 dark:border-red-500/50 dark:ring-red-500/20"
                          : "border-slate-200 focus-within:ring-2 focus-within:ring-fk-accent/30 dark:border-[oklch(0.33_0.022_264)]"
                      }`}
                    >
                      {canUse && (
                        <MicButton
                          size="sm"
                          title="Ask by voice"
                          armSignal={voiceArm}
                          onStatusChange={setVoiceStatus}
                          onTranscript={(text) => {
                            setVoiceError("");
                            handleVoiceTranscript(text);
                          }}
                          onError={(m) => {
                            setVoiceError(m);
                            setTimeout(() => setVoiceError(""), 8000);
                          }}
                        />
                      )}
                      {canUse && (
                        <>
                          <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf,image/png,image/jpeg,image/webp,image/gif"
                            className="hidden"
                            onChange={(e) => {
                              const f = e.target.files?.[0];
                              e.target.value = "";
                              if (f) analyzeLocalFile(f);
                            }}
                          />
                          <button
                            type="button"
                            title="Analyze a document — drop a PDF/image anywhere on this panel, or pick one"
                            aria-label="Attach a document to analyze"
                            disabled={docBusy}
                            onClick={() => fileInputRef.current?.click()}
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:bg-slate-200/70 hover:text-violet-600 disabled:opacity-50 dark:text-slate-400 dark:hover:bg-[oklch(0.26_0.028_264)] dark:hover:text-violet-300"
                          >
                            <FontAwesomeIcon
                              icon={docBusy ? faCircleNotch : faPaperclip}
                              className={`text-[13px] ${docBusy ? "animate-spin" : ""}`}
                            />
                          </button>
                        </>
                      )}
                      <input
                        ref={inputRef}
                        type="text"
                        placeholder={
                          voiceStatus === "recording"
                            ? "Listening — pause to send, tap to stop"
                            : voiceStatus === "transcribing"
                              ? "Transcribing…"
                              : canUse
                                ? askDoc
                                  ? "Ask about this document…"
                                  : "Say it or type it…"
                                : "Sign in to use Copilot"
                        }
                        className="min-w-0 flex-1 border-0 bg-transparent px-1 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 dark:text-slate-100"
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleInputKey}
                      />
                      <button
                        type="button"
                        aria-label="Send message"
                        disabled={!input.trim() || !canUse}
                        onClick={handleSendClick}
                        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors duration-150 ${
                          input.trim() && canUse
                            ? "bg-fk-accent text-white shadow-sm hover:opacity-95 dark:bg-blue-600"
                            : "bg-slate-200 text-slate-400 dark:bg-[oklch(0.3_0.024_264)] dark:text-slate-500"
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={loading ? faCircleNotch : faPaperPlane}
                          className={`text-[13px] ${loading ? "animate-spin" : ""}`}
                        />
                      </button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>,
          overlayRoot
        )}
    </>
  );
}
